import { findOffsetsOfTokenWithContext } from '../../byte-reader/findOffsetsOfTokenWithContext';
import type { RandomAccessFileReader } from '../../byte-reader/types';
import { ResultCode } from '../../consts';
import { GfShouldNotHappenError } from '../../errors';
import { doKeyInc } from '../../utils/map';
import type { GoodBadResult } from '../../utils/result';
import { bad, good } from '../../utils/result';
import { EMboxFromSig, identifyFromLine } from './identifyFromLine';

export type IMboxMessageBounds = {
	start: number;
	endPlusOne: number;
	offsetsOfEscapedFrom?: number[];
};

export type IMboxParseResult = {
	messageSplits: IMboxMessageBounds[];
	knownFromGeneratorAggs: Map<EMboxFromSig, number>;
	unescapeRecommendation: 'DO' | 'SKIP';
	unescapeConfidence: 'VERY-HIGH' | 'HIGH' | 'LOW' | 'UNKNOWN';
	escapeMatchAggs: Map<number, number>;
	numEscapedFromMatches: number;
	numNoNewlineNoEscapeFromMatches: number;
	numSkippedNewlineFromMatches: number;
};

const bytesBeforeFrom = 8;
const maxFromLineBytes = 256;
const rxPreNewline = /(?:\r?\n|\r)$/;
const rxPostLineAndFirstHeader = /^([^\r\n]+)(\r?\n|\r)([!-9;-~]+:)?/;

export async function parseMboxFromSplits(
	fileReader: RandomAccessFileReader
): Promise<
	GoodBadResult<IMboxParseResult & { numMessages: number }, { code: ResultCode; info?: string }>
> {
	const startBuf = await fileReader.readBytes(0, Math.min(maxFromLineBytes, fileReader.size));
	const startStr = startBuf.toString('binary');

	if (!startStr.startsWith('From ')) {
		return bad({ code: ResultCode.BAD_FORMAT, info: 'Expected .mbox to start with "From "' });
	}

	const firstParsed = parseFromLine(startStr.slice(5));
	if (!firstParsed) {
		return bad({
			code: ResultCode.BAD_FORMAT,
			info: 'Expected .mbox to start with "From " line followed by an email header',
		});
	}

	const {
		from: firstFrom,
		newline: firstNewline,
		header: firstHeader,
		fromSig: firstGen,
	} = firstParsed;

	if (firstGen === EMboxFromSig.UNKNOWN) {
		return bad({
			code: firstHeader ? ResultCode.UNSUPPORTED : ResultCode.BAD_FORMAT,
			info: `.mbox file has unknown "From " line format${
				firstHeader ? ', but appears to have a header' : ' - likely not MBOX'
			}`,
		});
	}

	if (!firstHeader) {
		return bad({
			code: ResultCode.BAD_FORMAT,
			info: '.mbox has known "From " line format, but no header',
		});
	}

	const results: IMboxParseResult = {
		messageSplits: [],
		knownFromGeneratorAggs: new Map(),
		unescapeRecommendation: 'SKIP',
		unescapeConfidence: 'UNKNOWN',
		escapeMatchAggs: new Map(),
		numEscapedFromMatches: 0,
		numNoNewlineNoEscapeFromMatches: 0,
		numSkippedNewlineFromMatches: 0,
	};

	doKeyInc(results.knownFromGeneratorAggs, firstGen);
	let nextEmailStartIdx = 5 + firstFrom.length + firstNewline.length;
	let offsetsOfEscapedFrom: number[] = [];

	const matches = await findOffsetsOfTokenWithContext({
		fileReader,
		needle: 'From ',
		encoding: 'binary',
		bytesBefore: bytesBeforeFrom,
		bytesAfter: maxFromLineBytes - 5,
	});

	if (!matches.length || matches[0].offset !== 0) {
		throw new GfShouldNotHappenError('MBOX starts with "From " but no first match');
	}

	for (const match of matches.slice(1)) {
		const preNewlineMatch = rxPreNewline.exec(match.before);

		if (!preNewlineMatch) {
			// Not a "From " that we are using to split, but perhaps an escaped one
			if (match.before.endsWith('>')) {
				const escapeMatch = /[\r\n]>+$/.exec(match.before);
				if (escapeMatch) {
					doKeyInc(results.escapeMatchAggs, escapeMatch[0].length - 1);
					++results.numEscapedFromMatches;

					// Add the escaped ">From " index to the list
					offsetsOfEscapedFrom.push(match.offset);
				} else {
					++results.numNoNewlineNoEscapeFromMatches;
				}
			} else {
				++results.numNoNewlineNoEscapeFromMatches;
			}
			continue;
		}

		const parsed = parseFromLine(match.after);
		if (!parsed) {
			return bad({
				code: ResultCode.UNSUPPORTED,
				info: '.mbox has "[\\r\\n]From " without a following newline',
			});
		}

		if (parsed.fromSig === EMboxFromSig.UNKNOWN) {
			if (parsed.header) {
				return bad({
					code: ResultCode.UNSUPPORTED,
					info: '.mbox has unknown "From " format with valid header',
				});
			}
			++results.numSkippedNewlineFromMatches;
			continue;
		}

		if (!parsed.header) {
			return bad({
				code: ResultCode.BAD_FORMAT,
				info: '.mbox has known "From " format but no header',
			});
		}

		// Valid split found
		const message: IMboxMessageBounds = {
			start: nextEmailStartIdx,
			endPlusOne: match.offset - preNewlineMatch[0].length,
		};
		if (offsetsOfEscapedFrom.length) {
			message.offsetsOfEscapedFrom = offsetsOfEscapedFrom;
			offsetsOfEscapedFrom = [];
		}
		results.messageSplits.push(message);

		nextEmailStartIdx = match.offset + 5 + parsed.from.length + parsed.newline.length;
		doKeyInc(results.knownFromGeneratorAggs, parsed.fromSig);
	}

	// Push final message... avoid extra newline that is added most of the time
	const fileEndBuf = await fileReader.readBytes(Math.max(0, fileReader.size - 6), 6);
	const fileEndStr = fileEndBuf.toString('binary');
	const fileEndDoubleNewlineMatch = /\r?\n(\r?\n)$/.exec(fileEndStr);

	const message: IMboxMessageBounds = {
		start: nextEmailStartIdx,
		endPlusOne: fileEndDoubleNewlineMatch
			? fileReader.size - fileEndDoubleNewlineMatch[1].length
			: fileReader.size,
	};

	if (offsetsOfEscapedFrom.length) {
		message.offsetsOfEscapedFrom = offsetsOfEscapedFrom;
		offsetsOfEscapedFrom = [];
	}
	results.messageSplits.push(message);

	// Finalize Recommendations
	const numMessages = results.messageSplits.length;
	if (results.numEscapedFromMatches === 0) {
		results.unescapeRecommendation = 'SKIP';
		results.unescapeConfidence = 'VERY-HIGH';
	} else if (results.numSkippedNewlineFromMatches > 0) {
		// numEscapedFromMatches !== 0
		results.unescapeRecommendation = 'SKIP';
		results.unescapeConfidence = 'LOW';
	} else {
		// numEscapedFromMatches !== 0
		// numSkippedNewlineFromMatches === 0
		results.unescapeRecommendation = 'DO';
		const googleGens = new Set([
			EMboxFromSig.GoogleVault,
			EMboxFromSig.GoogleTakeout,
			EMboxFromSig.GoogleTakeoutChat,
		]);
		let googleCount = 0;
		results.knownFromGeneratorAggs.forEach((count, gen) => {
			if (googleGens.has(gen)) googleCount += count;
		});

		results.unescapeConfidence = googleCount === numMessages ? 'VERY-HIGH' : 'HIGH';
	}

	return good({ numMessages, ...results });
}

/**
 * Helper to validate if a "From " match is followed by a valid MBOX structure
 */
function parseFromLine(text: string) {
	const match = rxPostLineAndFirstHeader.exec(text);
	if (!match) {
		return null;
	}
	const [, from, newline, header] = match as any as [string, string, string, string | undefined];
	return { from, newline, header, fromSig: identifyFromLine(from) };
}
